import moment from 'moment';
import { 
	VisitorEngagementLevel, 
	AiInsight,
	AiInsightsFilters
} from './context/AiInsightsApiTypesServices';
import { getFormattedNumber, getFormattedPercent } from '../../../Util/numberFormats';

export interface Period {
	start: moment.Moment;
	end: moment.Moment;
}

export interface PeriodGroup {
	firstPeriode: Period;
	secondPeriode?: Period;
	isSingleDay?: boolean;
}

export type EngagementLevel = {
    value: VisitorEngagementLevel;
    label: string;
}

export const engagementLevels:Array<EngagementLevel> = [
	{
		value : 'X',
		label : 'Lost'
	},
	{
		value : 'C',
		label : 'Consideration'
	},
	{
		value : 'D',
		label : 'Discover'
	},
	{
		value : 'I',
		label : 'Intention'
	},
	{
		value : 'A',
		label : 'Purchase'
	},
	{
		value : 'E',
		label : 'Experience'
	},
	{
		value : 'R',
		label : 'Recurring purchase'
	},
	{
		value : '',
		label : 'none'
	},
];
export const sourceTypes = [
	{ value: 'Direct', label: 'Direct' },
	{ value: 'Organic', label: 'Organic' },
	{ value: 'Referral', label: 'Referral' },
	{ value: 'Social', label: 'Social' },
	{ value: 'Paid', label: 'Paid' },
	{ value: 'Email', label: 'Email' },
	{ value: 'Display', label: 'Display' },
	{ value: 'Other', label: 'Other' },
];
export const dimensions = [
	{ value: 'VisEngLevel', label: 'Visitor Engagement Level' },
	{ value: 'FirstVisEngLevel', label: 'First Visitor Engagement Level' },
	{ value: 'LastVisEngLevel', label: 'Last Visitor Engagement Level' },
	{ value: 'PageType', label: 'Page Type' },
	{ value: 'FirstPageType', label: 'First Page Type' },
	{ value: 'LastPageType', label: 'Last Page Type' },
	{ value: 'Tenant', label: 'Tenant' },
];
export const devices = [
	{
		value : 'Desktop',
		label : 'Desktop'
	},
	{
		value : 'Mobile',
		label : 'Mobile'
	},
	{
		value : 'Tablet',
		label : 'Tablet'
	},
];
export const insightTypes = [
	// { value: 'WebsiteTraffic', label: 'Website Traffic' },
	// { value: 'Product', label: 'Product' },
	// { value: 'AbandonedBasket', label: 'Abandoned Basket' },
	{ value: 'Cart', label: 'Cart' },
	{ value: 'Conversions', label: 'Conversions' },
	{ value: 'PageViews', label: 'Page views' },
	{ value: 'ProductsBought', label: 'Products bought' },
	{ value: 'Visitors', label: 'Visitors' },
	{ value: 'Turnover', label: 'Turnover' },
	{ value: 'Transactions', label: 'Transactions' },
	{ value: 'Sessions', label: 'Sessions' },
	{ value: 'BrowsingTime', label: 'Browsing time' },
];

export const STORAGE_KEY = 'aiInsightsFilters';

export function applyFilters(data: AiInsight[], filters: AiInsightsFilters): AiInsight[] {
	if (!filters) return data;
	return data.filter(item => {
		const debug: Record<string, unknown> = {};
		let match = true;
		if (filters.engagementLevel && filters.engagementLevel.length > 0) {
			const filterVals = filters.engagementLevel.map((v: string) => v.toString().toLowerCase());
			const itemVals = [
				(item.dimensions?.VisitorEngagement ?? '').toString().toLowerCase(),
				(item.dimensions?.FirstVisEngLevel ?? '').toString().toLowerCase(),
				(item.dimensions?.LastVisEngLevel ?? '').toString().toLowerCase(),
			];
			match = itemVals.some(val => filterVals.includes(val));
			debug.engagementLevel = {itemVals, filterVals, match};
		}
		if (match && filters.device && filters.device.length > 0) {
			const itemVal = (item.dimensions?.Device ?? item.dimensions?.DeviceType ?? '').toString().toLowerCase();
			const filterVals = filters.device.map((v: string) => v.toString().toLowerCase());
			match = filterVals.includes(itemVal);
			debug.device = {itemVal, filterVals, match};
		}
		if (match && filters.sourceType && filters.sourceType.length > 0) {
			const itemVal = (item.dimensions?.TrafficSource ?? '').toString().toLowerCase();
			const filterVals = filters.sourceType.map((v: string) => v.toString().toLowerCase());
			match = filterVals.includes(itemVal);
			debug.sourceType = {itemVal, filterVals, match};
		}

		if (match && filters.insightType && filters.insightType.length > 0) {
			const itemVal = (item.category ?? '').toString().toLowerCase();
			const filterVals = filters.insightType.map((v: string) => v.toString().toLowerCase());
			match = filterVals.includes(itemVal);
			debug.insightType = {itemVal, filterVals, match};
		}
		if (match && filters.tenant && filters.tenant.length > 0) {
			const filterVal = Array.isArray(filters.tenant)
				? filters.tenant[0]?.toString().toLowerCase()
				: filters.tenant.toString().toLowerCase();
			if (filterVal !== 'all' && filterVal !== 'notenant' && filterVal !== '') {
				const itemVal = (item.dimensions?.Tenant ?? '').toString().toLowerCase();
				match = filterVal === itemVal;
				debug.tenant = {itemVal, filterVal, match};
			}
		}
		if (match && filters.pageType && filters.pageType.length > 0) {
			const filterVals = filters.pageType.map((v: string) => v.toString().toLowerCase());
			const itemVals = [
				(item.dimensions?.FirstPageType ?? '').toString().toLowerCase(),
				(item.dimensions?.PageType ?? '').toString().toLowerCase(),
				(item.dimensions?.LastPageType ?? '').toString().toLowerCase(),
			];
			match = itemVals.some(val => filterVals.includes(val));
			debug.pageType = {itemVals, filterVals, match};
		}

		// others (logique OU)
		if (match && Array.isArray(filters.others) && filters.others.length > 0) {
			match = filters.others.some((filterVal: string) => {
				const [key, id] = filterVal.split('__');
				return item.dimensions && Object.prototype.hasOwnProperty.call(item.dimensions, key) && item.dimensions[key] === id;
			});
			debug.others = {others: filters.others, match};
		}

		return match;
	});
}
export function getInitialFilters() {
	const STORAGE_KEY = 'aiInsightsFilters';
	const emptyFilters = {
		engagementLevel: [],
		device: [],
		sourceType: [],
		dimension: [],
		insightType: [],
		pageType: []
	};
	if (typeof window === 'undefined' || !window.location) {
		return emptyFilters;
	}
	const location = window.location;
	const params = new URLSearchParams(location.search ?? '');
	const urlFilters = {
		engagementLevel: params.get('engagementLevel')?.split(',') ?? [],
		device: params.get('device')?.split(',') ?? [],
		sourceType: params.get('sourceType')?.split(',') ?? [],
		dimension: params.get('dimension')?.split(',') ?? [],
		insightType: params.get('insightType')?.split(',') ?? [],
		pageType: params.get('pageType')?.split(',') ?? [] 
	};
	if (Object.values(urlFilters).some(arr => arr.length > 0)) return urlFilters;
	try {
		const saved = localStorage.getItem(STORAGE_KEY);
		return saved ? { ...emptyFilters, ...JSON.parse(saved) } : emptyFilters;
	} catch { return emptyFilters; }
}

/**
 * Compare two arrays for shallow equality (same length, same values in order)
 */
export function areArraysEqual<T>(a: T[], b: T[]): boolean {
	if (a.length !== b.length) return false;
	return a.every((v, i) => v === b[i]);
}

const numbersRandom:number[] = [];
for (let i = 0; i < 3; i++){
	const min = 20;
	const max = 70;
	const newRandomNumber: number = Math.floor(Math.random() * (max - min + 1)) + min;
	if(numbersRandom.includes(newRandomNumber)){
		i--;
	}else{
		numbersRandom.push(newRandomNumber);
	}
}
const points = [...numbersRandom];
const numInterpolatedPoints = 30; // Réduire le nombre de points intermédiaires
const interpolatedData: number[] = [];

// Fonction pour une interpolation plus douce Interpolation Linéaire avec Assouplissement
const easeInOutQuad = (t: number): number => {
	return t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
};
for (let i = 0; i < points.length - 1; i++) {
	const start = points[i];
	const end = points[i + 1];
			
	for (let j = 0; j < numInterpolatedPoints; j++) {
		const ratio = j / numInterpolatedPoints;
		const interpolatedValue = start + (end - start) * easeInOutQuad(ratio);
		interpolatedData.push(interpolatedValue);
	}
}
interpolatedData.push(points[points.length - 1]);

export function getPeriodRange(dateStr, period) {
	const endDate = moment(dateStr);
	const startDate = moment(dateStr);

	const subtractDays = {
		Last7Days: 6,
		Last30Days: 29,
		Last90Days: 89,
		Last365Days: 364,
		LastDay: 0,
	};

	if (subtractDays[period] !== undefined) {
		startDate.subtract(subtractDays[period], 'days');
	}

	const firstPeriode:Period = {
		start: startDate,
		end: endDate,
	};

	const subtract = period === 'LastDay' ? 7 : 1;
	const secondEndDate = moment(startDate).subtract(subtract, 'days');
	const secondStartDate = moment(secondEndDate);

	if (subtractDays[period] !== undefined) {
		secondStartDate.subtract(subtractDays[period], 'days');
	}

	const secondPeriode:Period = {
		start: secondStartDate,
		end: secondEndDate,
	};

	return {
		firstPeriode: firstPeriode,
		secondPeriode: secondPeriode,
		isSingleDay: period === 'LastDay'
	};
}

export const getFormattedDate = (date):string => {
	const six_months_ago = moment().subtract(6, 'months');
	if (date.isBefore(six_months_ago)) {
		return date.format('D MMM YYYY');
	}
	return date.format('D MMM');
};

export const getFormattedValue = (v, otherValue, type) => {
	if (type === 'Percentage') {
		return getFormattedPercent(v * 100, 2);
	}
	if (type === 'Amount') {
		return getFormattedNumber(v, 2, '€');
	}
	let isInteger = Number.isInteger(v);
	if (typeof otherValue === 'number' && !Number.isInteger(otherValue)) {
		isInteger = false;
	}
	return getFormattedNumber(v, isInteger ? 0 : 2, '');
};


/**
 * Génère les labels d'intervalle pour les données selon la logique métier :
 * - Le dernier point = période actuelle (ex: 24 Jun - 30 Jun)
 * - L'avant-dernier point = période qui couvre les historyDepth périodes précédentes
 * - Les autres points = même logique, chaque point couvre une fenêtre de historyDepth périodes
 * @param endDate Date de fin de la période actuelle
 * @param periodType Type de période (Last7Days, Last30Days, etc.)
 * @param dataLength Nombre de points de données (peut être différent de historyDepth)
 * @param historyDepth Profondeur d'historique pour les fenêtres glissantes
 * @returns Tableau de labels formatés
 */
export function getIntervalLabelsForData(endDate: Date | moment.Moment, periodType: string, dataLength: number, historyDepth?: number): string[] {
	if (!dataLength) return [];
	
	const subtractDays = {
		Last7Days: 6,
		Last30Days: 29,
		Last90Days: 89,
		Last365Days: 364,
		LastDay: 0,
	};
	
	const duration = subtractDays[periodType] !== undefined ? subtractDays[periodType] : 0;
	const end = moment.isMoment(endDate) ? endDate.clone() : moment(endDate);
	const labels: string[] = [];

	if (!historyDepth || historyDepth <= 1) {
		for (let i = 0; i < dataLength; i++) {
			const pointsFromEnd = dataLength - 1 - i;
			const intervalEnd = end.clone().subtract(pointsFromEnd * (duration + 1), 'days');
			const intervalStart = intervalEnd.clone().subtract(duration, 'days');
			labels.push(`${getFormattedDate(intervalStart)} - ${getFormattedDate(intervalEnd)}`);
		}
		return labels;
	}

	for (let i = 0; i < dataLength; i++) {
		if (i === dataLength - 1) {
			// Dernier point (vert) : période actuelle
			const intervalEnd = end.clone();
			const intervalStart = intervalEnd.clone().subtract(duration, 'days');
			labels.push(`${getFormattedDate(intervalStart)} - ${getFormattedDate(intervalEnd)}`);
		} else {
			// Tous les autres points : générer les labels individuels pour chaque point
			const pointsFromEnd = dataLength - 1 - i;
			const intervalEnd = end.clone().subtract(pointsFromEnd * (duration + 1), 'days');
			const intervalStart = intervalEnd.clone().subtract(duration, 'days');
			labels.push(`${getFormattedDate(intervalStart)} - ${getFormattedDate(intervalEnd)}`);
		}
	}
	
	return labels;
}


export function getPeriodTypeFromDuration(startDate: Date | moment.Moment, endDate: Date | moment.Moment): string {
	const start = moment.isMoment(startDate) ? startDate : moment(startDate);
	const end = moment.isMoment(endDate) ? endDate : moment(endDate);
	const duration = end.diff(start, 'days');
	
	if (duration === 0) return 'LastDay';
	if (duration === 6) return 'Last7Days';
	if (duration === 29) return 'Last30Days';
	if (duration === 89) return 'Last90Days';
	if (duration === 364) return 'Last365Days';
	return 'Last7Days';
}

/**
 * Calcule l'intervalle de comparaison global basé sur historyDepth
 * Utilisé pour déterminer les couleurs des points et l'affichage dans TableMetric
 * La logique métier : l'intervalle de comparaison couvre les historyDepth dernières périodes
 * avant la période actuelle (excluant la période actuelle)
 */
export function getComparisonInterval(endDate: Date | moment.Moment, periodType: string, dataLength: number, historyDepth?: number): { start: moment.Moment; end: moment.Moment } | null {
	if (!historyDepth || historyDepth <= 1 || !dataLength) return null;
	
	const subtractDays = {
		Last7Days: 6,
		Last30Days: 29,
		Last90Days: 89,
		Last365Days: 364,
		LastDay: 0,
	};
	
	const duration = subtractDays[periodType] !== undefined ? subtractDays[periodType] : 0;
	const end = moment.isMoment(endDate) ? endDate.clone() : moment(endDate);
	
	const availableHistoryPoints = dataLength - 1;
	
	if (availableHistoryPoints <= 0) return null;

	let startBlueIndex, endBlueIndex;
	if (historyDepth >= availableHistoryPoints) {
		startBlueIndex = 0;
		endBlueIndex = dataLength - 2;
	} else {
		startBlueIndex = dataLength - 1 - historyDepth;
		endBlueIndex = dataLength - 2;
	}
	const intervalStart = end.clone()
		.subtract((dataLength - 1 - startBlueIndex) * (duration + 1), 'days')
		.subtract(duration, 'days');
	const intervalEnd = end.clone()
		.subtract((dataLength - 1 - endBlueIndex) * (duration + 1), 'days');
	return { start: intervalStart, end: intervalEnd };
}

/**
 * Détermine si un point spécifique doit être coloré en bleu (dans l'intervalle de comparaison)
 * @param pointIndex Index du point dans le tableau de données (0 = premier point, dataLength-1 = dernier point)
 * @param dataLength Nombre total de points de données
 * @param historyDepth Profondeur d'historique pour les fenêtres glissantes
 * @returns true si le point doit être coloré en bleu, false sinon
 */
export function shouldPointBeBlue(pointIndex: number, dataLength: number, historyDepth?: number): boolean {

	if (pointIndex === dataLength - 1) return false;
	
	if (!historyDepth || historyDepth <= 1 || !dataLength) return false;
	
	const availableHistoryPoints = dataLength - 1;
	
	if (availableHistoryPoints <= 0) return false;
	
	if (historyDepth >= availableHistoryPoints) {
		return pointIndex >= 0 && pointIndex <= dataLength - 2;
	}
	
	const startBlueIndex = dataLength - 1 - historyDepth;
	const endBlueIndex = dataLength - 2;
	
	return pointIndex >= startBlueIndex && pointIndex <= endBlueIndex;
}